import GLightbox from 'glightbox';
import { FilterBar, itemValues, loadOtherPages, setUrlParam, shouldEnhance } from '@theme/gallery-feed';

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.inspiration-gallery').forEach(initGallery);
});

const DESKTOP_MQ = '(min-width: 769px)';
// Photos added to the grid per scroll step. Images are lazy, so this mainly keeps
// the DOM (and the justified rows) small until someone scrolls.
const BATCH = 60;

function initGallery(container) {
  const grid = container.querySelector('.inspiration-gallery__grid');
  // Liquid can only list 250 photos per page. Every photo — this page's and the
  // later pages fetched in the background — lives in allItems in page order; only
  // the first `shown` of the current filter's matches sit in the grid.
  const enhance = shouldEnhance(container);
  const allItems = Array.from(grid.querySelectorAll('.inspiration-gallery__item'));
  let activeFilter = 'all';
  let matching = allItems;
  let shown = allItems.length;
  let pagesLoaded = !enhance;
  let pagesPromise = Promise.resolve();
  let sentinel = null;
  let status = null;

  let lightbox = null;
  let productsMap = [];
  let stlButton = null;
  let stlPanel = null;
  let stlBackdrop = null;
  let stlCounter = null;
  let currentSlideIndex = 0;
  let totalSlides = 0;

  // Layout is pure CSS (justified rows — see sections/inspiration-gallery.liquid),
  // so the page order IS the visual order and the lightbox can follow it directly.

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (document.querySelector('.glightbox-container')) {
        updateSTL(currentSlideIndex);
        if (isDesktop()) injectAllSidePanels();
      }
    }, 80);
  });

  // --- Lightbox helpers ---

  function parseProducts(link) {
    const attr = link.dataset.products;
    if (!attr) return [];
    try {
      return JSON.parse(attr);
    } catch (e) {
      return [];
    }
  }

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // --- Product card markup (used for both desktop description and mobile sheet) ---

  function buildProductCardsHtml(products) {
    return products
      .map(function (product) {
        var imgHtml = product.image
          ? '<img src="' +
            escapeHtml(product.image) +
            '" alt="" class="stl-panel__product-image" width="120" height="120" loading="lazy">'
          : '';
        return (
          '<a href="' +
          escapeHtml(product.url) +
          '" class="stl-panel__product">' +
          imgHtml +
          '<div class="stl-panel__product-info">' +
          '<span class="stl-panel__product-title">' +
          escapeHtml(product.title) +
          '</span>' +
          (product.price
            ? '<span class="stl-panel__product-price">' + escapeHtml(product.price) + '</span>'
            : '') +
          '</div></a>'
        );
      })
      .join('');
  }

  function injectSidePanel(slideEl, products) {
    if (!slideEl || !products || products.length === 0) return;
    var inner = slideEl.querySelector('.ginner-container');
    if (!inner) return;
    var existing = inner.querySelector('.stl-side-panel');
    if (existing) existing.parentNode.removeChild(existing);
    var panel = document.createElement('div');
    panel.className = 'stl-side-panel';
    panel.innerHTML =
      '<h4 class="stl-panel__heading">Shop This Look</h4>' +
      '<div class="stl-panel__products">' +
      buildProductCardsHtml(products) +
      '</div>';
    inner.appendChild(panel);
  }

  // Size the slide image to FIT: as tall as 80vh allows, but never wider than the
  // space left beside the Shop This Look panel (and the arrows). Inline !important
  // so it wins against GLightbox's own inline sizing. The panel matches the image
  // height so the two read as one card. (A forced 80vh height pushed wide photos
  // off the side of the screen — fixed 2026-10-28.)
  var SLIDE_MAX_H = 0.8;   // of the viewport height
  var SLIDE_GUTTER = 160;  // both sides together: room for the prev/next arrows

  function sizeSlideImage(slideEl, tries) {
    if (!slideEl || !isDesktop()) return;
    // GLightbox's image slides create a plain <img> with no class — it lives
    // inside .gslide-media (which has the .gslide-image class on it).
    var media = slideEl.querySelector('.gslide-media.gslide-image') ||
      slideEl.querySelector('.gslide-media');
    if (!media) return;
    var img = media.querySelector('img');
    if (!img) {
      // Image not yet inserted — retry briefly (slide_after_load covers the rest)
      tries = tries || 0;
      if (tries < 20) setTimeout(function () { sizeSlideImage(slideEl, tries + 1); }, 50);
      return;
    }
    var panel = slideEl.querySelector('.stl-side-panel');
    var apply = function () {
      var maxH = Math.round(window.innerHeight * SLIDE_MAX_H);
      var maxW = window.innerWidth - SLIDE_GUTTER - (panel ? panel.offsetWidth : 0);
      var ratio = img.naturalWidth && img.naturalHeight
        ? img.naturalWidth / img.naturalHeight
        : 0;
      // Before the image loads its shape is unknown: cap both ways and let the
      // browser keep the proportions; the load handler sets exact pixels.
      var h = ratio ? Math.round(Math.min(maxH, maxW / ratio)) : maxH;
      var w = ratio ? Math.round(h * ratio) : null;
      img.style.setProperty('height', ratio ? h + 'px' : 'auto', 'important');
      img.style.setProperty('width', w ? w + 'px' : 'auto', 'important');
      img.style.setProperty('max-height', maxH + 'px', 'important');
      img.style.setProperty('max-width', maxW + 'px', 'important');
      img.style.setProperty('min-height', '0', 'important');
      img.style.setProperty('min-width', '0', 'important');
      img.style.setProperty('object-fit', 'contain', 'important');
      img.style.setProperty('display', 'block', 'important');
      media.style.setProperty('height', 'auto', 'important');
      media.style.setProperty('max-height', maxH + 'px', 'important');
      media.style.setProperty('width', 'auto', 'important');
      media.style.setProperty('max-width', 'none', 'important');
      if (panel) panel.style.setProperty('height', (ratio ? h : maxH) + 'px');
    };
    apply();
    // Re-apply after the image actually loads — natural dimensions become known
    // and some CSS can re-compute at that point.
    if (!img.complete) {
      img.addEventListener('load', apply, { once: true });
    }
  }

  // Only slides GLightbox has already loaded: with hundreds of slides, touching the
  // empty ones would start a retry timer for each.
  function injectAllSidePanels() {
    if (!isDesktop()) return;
    var slides = document.querySelectorAll('.glightbox-container .gslide');
    slides.forEach(function (slide, i) {
      if (!slide.querySelector('.gslide-media img')) return;
      injectSidePanel(slide, productsMap[i]);
      sizeSlideImage(slide);
    });
  }

  // --- Mobile-only UI (bottom sheet + trigger) ---

  function createSTLButton() {
    var btn = document.createElement('button');
    btn.className = 'stl-trigger';
    btn.textContent = 'Shop This Look';
    btn.type = 'button';
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      showMobilePanel();
    });
    return btn;
  }

  function createSTLPanel() {
    var panel = document.createElement('aside');
    panel.className = 'stl-panel';
    panel.innerHTML =
      '<button class="stl-panel__close" type="button" aria-label="Close panel">' +
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
      '</button>' +
      '<h4 class="stl-panel__heading">Shop This Look</h4>' +
      '<div class="stl-panel__products"></div>';
    panel.querySelector('.stl-panel__close').addEventListener('click', function (e) {
      e.stopPropagation();
      hideMobilePanel();
    });
    panel.addEventListener('click', function (e) {
      e.stopPropagation();
    });
    return panel;
  }

  function createSTLBackdrop() {
    var bd = document.createElement('div');
    bd.className = 'stl-backdrop';
    bd.addEventListener('click', function (e) {
      e.stopPropagation();
      hideMobilePanel();
    });
    return bd;
  }

  function createCounter() {
    var c = document.createElement('div');
    c.className = 'inspiration-counter';
    return c;
  }

  function showMobilePanel() {
    if (!stlPanel || isDesktop()) return;
    stlPanel.classList.add('stl-panel--visible');
    if (stlBackdrop) stlBackdrop.classList.add('stl-backdrop--visible');
    if (stlButton) stlButton.classList.add('stl-trigger--hidden');
  }

  function hideMobilePanel() {
    if (stlPanel) stlPanel.classList.remove('stl-panel--visible');
    if (stlBackdrop) stlBackdrop.classList.remove('stl-backdrop--visible');
    if (stlButton && !isDesktop()) {
      var products = productsMap[currentSlideIndex] || [];
      if (products.length > 0) {
        stlButton.classList.remove('stl-trigger--hidden');
      }
    }
  }

  function updateSTL(index) {
    currentSlideIndex = index;
    var products = productsMap[index] || [];

    if (stlCounter) {
      stlCounter.textContent = totalSlides > 1 ? (index + 1) + ' / ' + totalSlides : '';
    }

    // On desktop, the side panel is rendered by GLightbox itself via `description`
    // — there's nothing for us to update here. We just refresh counter + mobile UI.
    if (isDesktop()) return;

    if (stlPanel) stlPanel.classList.remove('stl-panel--visible');
    if (stlBackdrop) stlBackdrop.classList.remove('stl-backdrop--visible');

    if (products.length === 0) {
      if (stlButton) stlButton.classList.add('stl-trigger--hidden');
      return;
    }

    if (stlPanel) {
      var productsEl = stlPanel.querySelector('.stl-panel__products');
      productsEl.innerHTML = buildProductCardsHtml(products);
    }
    if (stlButton) stlButton.classList.remove('stl-trigger--hidden');
  }

  function injectUI() {
    var glContainer = document.querySelector('.glightbox-container');
    if (!glContainer) return;

    removeUI();

    stlCounter = createCounter();
    glContainer.appendChild(stlCounter);

    // Mobile-only elements. CSS hides them at desktop widths anyway, but building
    // them only when needed keeps the DOM clean.
    if (!isDesktop()) {
      stlPanel = createSTLPanel();
      stlBackdrop = createSTLBackdrop();
      stlButton = createSTLButton();
      glContainer.appendChild(stlBackdrop);
      glContainer.appendChild(stlPanel);
      glContainer.appendChild(stlButton);
    }

    updateSTL(currentSlideIndex);
  }

  function removeUI() {
    [stlButton, stlPanel, stlBackdrop, stlCounter].forEach(function (el) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    });
    stlButton = null;
    stlPanel = null;
    stlBackdrop = null;
    stlCounter = null;
  }

  // --- Lightbox ---

  // Built on each open from the current filter's matches — every match, not just the
  // ones scrolled into the grid — so "next" carries on past the last loaded tile.
  function openLightbox(index) {
    if (lightbox) {
      lightbox.destroy();
      lightbox = null;
    }

    productsMap = [];
    var elements = matching.map(function (item) {
      var link = item.querySelector('.inspiration-gallery__link');
      productsMap.push(parseProducts(link));
      return {
        href: link.href,
        type: 'image',
        alt: link.getAttribute('aria-label') || '',
      };
    });
    totalSlides = elements.length;
    currentSlideIndex = index;

    lightbox = GLightbox({
      elements: elements,
      touchNavigation: true,
      loop: false,
      effect: 'fade',
      cssEfects: {
        fade: { in: 'fadeIn', out: 'fadeOut' },
      },
    });

    lightbox.on('open', function () {
      injectUI();
      // GLightbox builds slide DOM lazily — inject after a tick
      setTimeout(injectAllSidePanels, 0);
    });

    lightbox.on('slide_after_load', function (data) {
      if (isDesktop() && data && data.slide) {
        var idx = typeof data.index === 'number' ? data.index : currentSlideIndex;
        injectSidePanel(data.slide, productsMap[idx]);
        sizeSlideImage(data.slide);
      }
    });

    lightbox.on('slide_changed', function (data) {
      updateSTL(data.current.index);
      if (isDesktop()) injectAllSidePanels();
    });

    lightbox.on('close', function () {
      removeUI();
    });

    lightbox.openAt(index);
  }

  grid.addEventListener('click', function (e) {
    var link = e.target.closest('.inspiration-gallery__link');
    if (!link) return;
    var index = matching.indexOf(link.closest('.inspiration-gallery__item'));
    if (index < 0) return;
    e.preventDefault();
    openLightbox(index);
  });

  // --- Showing photos (infinite scroll) ---

  function matchesFilter(item) {
    return activeFilter === 'all' || itemValues(item).indexOf(activeFilter) !== -1;
  }

  function showMore() {
    if (shown >= matching.length) return;
    var next = matching.slice(shown, shown + BATCH);
    shown += next.length;
    grid.append.apply(grid, next);
  }

  function sentinelNearScreen() {
    return sentinel && sentinel.getBoundingClientRect().top < window.innerHeight + 800;
  }

  // Keep adding batches while the end of the grid is near the screen. The observer
  // only fires when the sentinel crosses the margin, so a batch too short to push it
  // back out (tall screen, small filter) needs this follow-up check.
  function fillToScreen() {
    if (!sentinelNearScreen()) return;
    if (shown < matching.length) {
      showMore();
      requestAnimationFrame(fillToScreen);
    } else if (!pagesLoaded) {
      setStatus('Loading more photos…');
      pagesPromise.then(fillToScreen);
    } else {
      setStatus('');
    }
  }

  function setStatus(text) {
    if (status) status.textContent = text;
  }

  // --- Filtering ---

  function filterBy(value) {
    activeFilter = value;
    matching = allItems.filter(matchesFilter);
    shown = Math.min(BATCH, matching.length);
    grid.replaceChildren.apply(grid, matching.slice(0, shown));
    requestAnimationFrame(fillToScreen);
  }

  var filters = new FilterBar(container, function (value) {
    filterBy(value);
    filters.setActive(value);
    setUrlParam('filter', value === 'all' ? null : value);
  });

  // A shared ?filter= link may name a value that only appears on a later page, so
  // this runs again once those are in.
  function applyUrlFilter() {
    var urlFilter = new URL(window.location.href).searchParams.get('filter');
    if (!urlFilter || urlFilter === activeFilter || !filters.ready || !filters.has(urlFilter)) return;
    filterBy(urlFilter);
    filters.setActive(urlFilter);
  }

  // --- Start ---

  filters.build(allItems);
  applyUrlFilter();

  if (enhance) {
    var pageLinks = container.querySelector('.inspiration-gallery__pages');
    if (pageLinks) pageLinks.hidden = true;

    sentinel = document.createElement('div');
    sentinel.className = 'inspiration-gallery__sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    status = document.createElement('p');
    status.className = 'inspiration-gallery__status';
    status.setAttribute('role', 'status');
    grid.after(sentinel, status);

    pagesPromise = loadOtherPages(container, '.inspiration-gallery__item').then(function (more) {
      allItems.push.apply(allItems, more);
      pagesLoaded = true;
      matching = allItems.filter(matchesFilter);
      filters.build(allItems);
      setStatus('');
      applyUrlFilter();
    });

    new IntersectionObserver(
      function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) fillToScreen();
      },
      { rootMargin: '0px 0px 800px 0px' }
    ).observe(sentinel);
  }
}
